import {html, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {container} from "tsyringe";
import {IssueTypeRepository} from "../../../domain/repositories/issue-type.repository";
import type {FormConfiguration} from "../../../advanced-form/form-configuration.type";
import {CreateIssueTypeCommand} from "../../../commands/create-issue-type.command";
import {UpdateIssueTypeCommand} from "../../../commands/update-issue-type.command";
import {IssueType} from "../../../domain/entities/issue-type.entity";
import {AbstractEntityDialogComponent} from "../../ha-dialog/abstract-entity-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogCommand} from "../../ha-dialog/dialog-command.interface";
import {get, translate} from "lit-translate";
import {NotEmpty} from "../../../advanced-form/validators/not-empty.validator";
import {FieldProxy} from "../../../advanced-form/field.proxy";
import {ROOT_TYPE_UIDS} from "../../../constants";
import type {ComboboxItem} from "../../_inputs/ha-combobox/combobox-item.interface";

@customElement(`ha-dialog-issue-type-edit`)
export class HaDialogIssueTypeEditComponent extends AbstractEntityDialogComponent<IssueType> {

    @property({type: String})
    public title = get('ha-boards-view.edit-type.header');
    protected _formSettings: FormConfiguration<IssueType>[] = [
        {
            // The root this type belongs to (ShortId). Every type created here is a child of a
            // root; the backend refuses a type without one.
            key: `parentUid`,
            type: String,
            initialValue: null,
            label: `Gruppierung`,
            validators: [new NotEmpty()]
        },
        {
            key: `description`,
            type: String,
            initialValue: ``,
            label: `Titel`,
            validators: []
        },
        {
            key: `include`,
            type: String,
            initialValue: ``,
            label: `Include`,
            validators: []
        },
        {
            key: `shortcut`,
            type: String,
            initialValue: ``,
            label: `Kürzel`,
            validators: []
        },
        {
            key: `cssClasses`,
            type: String,
            initialValue: ``,
            label: `CSS Klassen`,
            validators: []
        },
        {
            key: `showInHeader`,
            type: Boolean,
            initialValue: true,
            label: `Eingeblendet`,
            validators: []
        },
        {
            key: `color`,
            type: String,
            initialValue: `#ffffff`,
            validators: [],
            label: `Farbe`
        },
        {
            key: `textColor`,
            type: String,
            initialValue: null,
            validators: [],
            label: `Schriftfarbe`
        },
    ];

    protected _renderFormFooter(): unknown {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveButton}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveAndCloseButton}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker secondary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickResetButton}
                
                        data-label=${translate('general.reset')}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    protected _renderMainContent(): unknown {
        const bindTo = this.form?.bindToFn!;

        return html`
            <ha-form-issue-type>
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    <ha-combobox class="max9999-flexrow" data-type="parent"
                                 label=${translate('ha-boards-view.edit-type.parent')}
                                 .entity=${IssueType}
                                 .itemFilter=${HaDialogIssueTypeEditComponent._isRootItem}
                                 .value=${this._selectedRoot}
                                 ?readonly=${this._isParentLocked}
                                 @value-changed=${this._onParentChanged}
                    ></ha-combobox>
                    <ha-input-text class="max9999-flexrow" data-type="title" label=${translate('components.ha-dialog.title' )}
                                    ${bindTo(`description`)}
                                    @value-changed=${this._requestRender}
                    ></ha-input-text>
                    <ha-input-text class="max9999-flexrow" data-type="shortly" label=${translate('ha-boards-view.edit-type.shortly')}
                                    ${bindTo(`shortcut`)}
                    ></ha-input-text>
                    <ha-input-color class="max9999-flexrow" data-type="color"
                                    label=${translate('components.ha-dialog.color')}
                                    .entity=${IssueType}
                                    ${bindTo(`color`)}
                                    @value-changed=${this._requestRender}
                    ></ha-input-color>
                    ${this._renderTextColorField(this.form!.value.description ?? ``)}
                </main>
            </ha-form-issue-type>
        `;
    }

    /** Only the five roots are offered as a parent: the tree has two levels. */
    private static _isRootItem(item: ComboboxItem): boolean {
        return ROOT_TYPE_UIDS.includes(String(item.id));
    }

    /**
     * The root cannot change while issues name this type (deleted ones included); the backend
     * reports that as canMoveRoot. A new type has no issues.
     */
    private get _isParentLocked(): boolean {
        return !!this._entity && !this._entity.isNew() && this._entity.canMoveRoot === false;
    }

    /** The form field that holds the Gruppierung as a ShortId. */
    private get _parentField(): FieldProxy<string | null> | null {
        return this.form?.getField<string | null>(`parentUid`) as FieldProxy<string | null> | null;
    }

    /** The root entity the combobox shows; the repository's own instance, which the combobox matches by identity. */
    @state()
    private _selectedRoot: IssueType | null = null;

    /** Clears the shown root before rendering when the form holds none (reset, new type). */
    protected willUpdate(changedProperties: PropertyValues): void {
        super.willUpdate(changedProperties);
        if (!(this._parentField?.value ?? null)) {
            this._selectedRoot = null;
        }
    }

    /**
     * Keeps the shown root in step with the uid the form holds. canMoveRoot is not read here: the
     * /by-type row menu reads it when the menu opens (TypesView.buildRowContextMenu); an edit opened
     * any other way starts with whatever canMoveRoot was last read for this type (null if never,
     * otherwise possibly stale), and a refused move is then rolled back by
     * UpdateIssueTypeCommandHandler, which locks the picker once its permission read succeeds.
     */
    protected updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);

        const uid = this._parentField?.value ?? null;
        if (!uid || (this._selectedRoot?.uid ?? null) === uid) {
            return;
        }
        container.resolve(IssueTypeRepository).findOneById(uid)
            .catch(() => null)
            .then(root => {
                if (root && (this._parentField?.value ?? null) === uid) {
                    this._selectedRoot = root;
                }
            });
    }

    /** Writes the chosen Gruppierung's uid into the form; clearing the combobox clears it. */
    private _onParentChanged(event: CustomEvent<{ value: IssueType | null }>): void {
        const field = this._parentField;
        if (field) {
            field.value = event.detail.value?.uid ?? null;
        }
    }

    private _onClickResetButton(): void {
        this.form?.reset();
    }

    /** Saves the entity and dispatches a CLOSE command to dismiss the dialog. */
    private _onClickSaveAndCloseButton(): void {
        this._onClickSaveButton();
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }

    private _onClickSaveButton(): void {
        if (!this.form) {
            return;
        }

        const data = this.form.getChangedValues();

        if (!this._entity) {
            return;
        }

        Object.assign(this._entity, data);

        this.dispatchCommand(<DialogCommand<UpdateIssueTypeCommand | CreateIssueTypeCommand>>{
            command: DialogCommandName.OKAY,
            payload: this._entity.isNew()
                ? new CreateIssueTypeCommand(this._entity)
                : new UpdateIssueTypeCommand(this._entity)
        });
    }
}